import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, HeadingLevel } from 'docx';
import * as XLSX from 'xlsx';

// Template color configurations for PDF
const TEMPLATE_COLORS = {
  professional: {
    primary: [37, 99, 235],        // Blue
    secondary: [59, 130, 246],
    accent: [96, 165, 250],
    text: [30, 41, 59],
    muted: [100, 116, 139],
    headerBg: [239, 246, 255],
    tableBorder: [191, 219, 254],
    name: 'Professional'
  },
  modern: {
    primary: [51, 65, 85],         // Slate
    secondary: [71, 85, 105],
    accent: [99, 102, 241],
    text: [15, 23, 42],
    muted: [100, 116, 139],
    headerBg: [241, 245, 249],
    tableBorder: [203, 213, 225],
    name: 'Modern'
  },
  classic: {
    primary: [120, 113, 108],      // Warm gray
    secondary: [87, 83, 78],
    accent: [180, 83, 9],
    text: [41, 37, 36],
    muted: [120, 113, 108],
    headerBg: [254, 252, 232],
    tableBorder: [214, 211, 209],
    name: 'Classic'
  },
  minimal: {
    primary: [0, 0, 0],            // Black
    secondary: [64, 64, 64],
    accent: [82, 82, 91],
    text: [0, 0, 0],
    muted: [115, 115, 115],
    headerBg: [250, 250, 250],
    tableBorder: [229, 229, 229],
    name: 'Minimal'
  },
  excel: {
    primary: [22, 101, 52],        // Green
    secondary: [21, 128, 61],
    accent: [34, 197, 94],
    text: [0, 0, 0],
    muted: [75, 85, 99],
    headerBg: [220, 252, 231],
    tableBorder: [134, 239, 172],
    name: 'Excel Style'
  },
  contractor: {
    primary: [194, 65, 12],        // Orange
    secondary: [234, 88, 12],
    accent: [251, 146, 60],
    text: [23, 23, 23],
    muted: [82, 82, 91],
    headerBg: [255, 247, 237],
    tableBorder: [254, 215, 170],
    name: 'Contractor Pro'
  }
};

// Format currency helper
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

/**
 * GUARANTEED file download - uses multiple methods to ensure download works
 * This function will try everything possible to download the file
 */
const guaranteedDownload = (blob, filename) => {
  return new Promise((resolve, reject) => {
    console.log(`🔽 Starting download: ${filename} (${blob.size} bytes)`);
    
    // Create object URL
    const blobUrl = URL.createObjectURL(blob);
    
    // Method 1: Hidden iframe download (most reliable for modern browsers)
    const downloadViaIframe = () => {
      try {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        const link = iframeDoc.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        iframeDoc.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
        
        console.log(`✅ Download via iframe: ${filename}`);
        return true;
      } catch (e) {
        console.warn('Iframe method failed:', e);
        return false;
      }
    };

    // Method 2: Direct anchor click
    const downloadViaAnchor = () => {
      try {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.cssText = 'position:fixed;left:-9999px;top:-9999px;';
        document.body.appendChild(link);
        
        // Trigger click
        link.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        }));
        
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
        
        console.log(`✅ Download via anchor: ${filename}`);
        return true;
      } catch (e) {
        console.warn('Anchor method failed:', e);
        return false;
      }
    };

    // Method 3: window.open with blob
    const downloadViaWindow = () => {
      try {
        const newWindow = window.open(blobUrl, '_blank');
        if (newWindow) {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
          console.log(`✅ Opened in new window: ${filename}`);
          return true;
        }
        return false;
      } catch (e) {
        console.warn('Window method failed:', e);
        return false;
      }
    };

    // Method 4: Navigator.msSaveBlob for IE/Edge
    const downloadViaMsSave = () => {
      try {
        if (navigator.msSaveBlob) {
          navigator.msSaveBlob(blob, filename);
          console.log(`✅ Download via msSaveBlob: ${filename}`);
          return true;
        }
        return false;
      } catch (e) {
        console.warn('msSaveBlob method failed:', e);
        return false;
      }
    };

    // Try all methods in sequence
    if (downloadViaMsSave() || downloadViaAnchor() || downloadViaIframe() || downloadViaWindow()) {
      resolve(true);
    } else {
      reject(new Error('All download methods failed'));
    }
  });
};

/**
 * Generate comprehensive PDF report
 */
export const generatePDFReport = ({
  projectInfo = {},
  companyInfo = {},
  calculatorTotals = {},
  estimateTotals,
  grandTotals,
  materialItems,
  laborItems,
  equipmentItems,
  additionalCosts,
  otherCosts,
  settings = {},
  lineItemMarkups,
  template = 'professional'
}) => {
  // Use grandTotals if estimateTotals not provided (for compatibility)
  const totals = estimateTotals || grandTotals || {};
  
  // Get template colors
  const colors = TEMPLATE_COLORS[template] || TEMPLATE_COLORS.professional;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  let yPos = 20;

  // Helper function to add new page if needed
  const checkPageBreak = (neededSpace = 30) => {
    if (yPos > 270 - neededSpace) {
      doc.addPage();
      yPos = 20;
    }
  };

  // Title with template colors
  doc.setFontSize(22);
  doc.setTextColor(...colors.primary);
  doc.text('MASS MASTER PRO', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;
  doc.setFontSize(14);
  doc.setTextColor(...colors.muted);
  doc.text('PROJECT ESTIMATE', pageWidth / 2, yPos, { align: 'center' });
  yPos += 4;
  doc.setFontSize(9);
  doc.text(`Template: ${colors.name}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 15;

  // Project Information
  doc.setFontSize(12);
  doc.setTextColor(...colors.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('PROJECT INFORMATION', 14, yPos);
  yPos += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...colors.text);

  const projectData = [
    ['Project Name:', projectInfo.name || 'N/A'],
    ['Project Number:', projectInfo.number || 'N/A'],
    ['Date:', projectInfo.date || new Date().toLocaleDateString()],
    ['Location:', projectInfo.location || 'N/A'],
    ['Client:', projectInfo.client || projectInfo.clientName || 'N/A'],
    ['Client Email:', projectInfo.clientEmail || 'N/A'],
  ];

  projectData.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 14, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), 55, yPos);
    yPos += 6;
  });

  // Company Information
  yPos += 5;
  doc.setFontSize(12);
  doc.setTextColor(...colors.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('COMPANY INFORMATION', 14, yPos);
  yPos += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...colors.text);

  const companyData = [
    ['Company:', (companyInfo && companyInfo.name) || 'N/A'],
    ['Phone:', (companyInfo && companyInfo.phone) || 'N/A'],
    ['Email:', (companyInfo && companyInfo.email) || 'N/A'],
    ['License:', (companyInfo && companyInfo.license) || 'N/A'],
  ];

  companyData.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 14, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), 55, yPos);
    yPos += 6;
  });

  // Calculator Summary
  checkPageBreak(60);
  yPos += 10;
  doc.setFontSize(12);
  doc.setTextColor(...colors.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('CALCULATOR SUMMARY', 14, yPos);
  yPos += 8;
  doc.setTextColor(...colors.text);

  const calcItems = [];
  if (calculatorTotals.demo > 0) calcItems.push(['Demo Materials', formatCurrency(calculatorTotals.demo)]);
  if (calculatorTotals.piping > 0) calcItems.push(['Oil Field Piping', formatCurrency(calculatorTotals.piping)]);
  if (calculatorTotals.tanks > 0) calcItems.push(['Oil Field Tanks', formatCurrency(calculatorTotals.tanks)]);
  if (calculatorTotals.buildings > 0) calcItems.push(['Custom Buildings', formatCurrency(calculatorTotals.buildings)]);
  if (calculatorTotals.import > 0) calcItems.push(['Import Materials', formatCurrency(calculatorTotals.import)]);
  if (calculatorTotals.customImport > 0) calcItems.push(['Custom Import Volume', formatCurrency(calculatorTotals.customImport)]);
  if (calculatorTotals.excavation > 0) calcItems.push(['Excavation', formatCurrency(calculatorTotals.excavation)]);
  if (calculatorTotals.customExcavation > 0) calcItems.push(['Custom Excavation Volume', formatCurrency(calculatorTotals.customExcavation)]);
  if (calculatorTotals.trucking > 0) calcItems.push(['Trucking', formatCurrency(calculatorTotals.trucking)]);
  if (calculatorTotals.disposal > 0) calcItems.push(['Disposal', formatCurrency(calculatorTotals.disposal)]);
  if (calculatorTotals.civil > 0) calcItems.push(['Civil Construction', formatCurrency(calculatorTotals.civil)]);
  if (calculatorTotals.environmental > 0) calcItems.push(['Environmental', formatCurrency(calculatorTotals.environmental)]);

  if (calcItems.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Category', 'Amount']],
      body: calcItems,
      theme: 'grid',
      headStyles: { fillColor: colors.primary, textColor: 255 },
      alternateRowStyles: { fillColor: colors.headerBg },
      margin: { left: 14, right: 14 },
      tableWidth: 'auto',
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Additional Materials
  if (materialItems && materialItems.length > 0) {
    checkPageBreak(50);
    doc.setFontSize(12);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('ADDITIONAL MATERIALS', 14, yPos);
    yPos += 8;
    doc.setTextColor(...colors.text);

    const matBody = materialItems.map(item => [
      item.name,
      item.quantity,
      item.unit,
      formatCurrency(item.unitCost),
      formatCurrency(item.quantity * item.unitCost)
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Material', 'Qty', 'Unit', 'Unit Cost', 'Total']],
      body: matBody,
      theme: 'grid',
      headStyles: { fillColor: colors.secondary, textColor: 255 },
      alternateRowStyles: { fillColor: colors.headerBg },
      margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Labor
  if (laborItems && laborItems.length > 0) {
    checkPageBreak(50);
    doc.setFontSize(12);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('LABOR', 14, yPos);
    yPos += 8;
    doc.setTextColor(...colors.text);

    const laborBody = laborItems.map(item => {
      const regularCost = item.regularHours * item.hourlyRate;
      const overtimeCost = (item.overtimeHours || 0) * (item.overtimeRate || item.hourlyRate * 1.5);
      return [
        item.name,
        item.regularHours,
        formatCurrency(item.hourlyRate),
        item.overtimeHours || 0,
        formatCurrency(regularCost + overtimeCost)
      ];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['Position', 'Reg Hours', 'Rate', 'OT Hours', 'Total']],
      body: laborBody,
      theme: 'grid',
      headStyles: { fillColor: colors.secondary, textColor: 255 },
      alternateRowStyles: { fillColor: colors.headerBg },
      margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Equipment
  if (equipmentItems && equipmentItems.length > 0) {
    checkPageBreak(50);
    doc.setFontSize(12);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('EQUIPMENT', 14, yPos);
    yPos += 8;
    doc.setTextColor(...colors.text);

    const equipBody = equipmentItems.map(item => {
      let total = 0;
      let duration = '';
      if (item.rentalType === 'daily') {
        total = item.days * item.dailyRate;
        duration = `${item.days} days @ ${formatCurrency(item.dailyRate)}/day`;
      } else if (item.rentalType === 'weekly') {
        total = item.weeks * item.weeklyRate;
        duration = `${item.weeks} weeks @ ${formatCurrency(item.weeklyRate)}/week`;
      } else {
        total = item.flatRate || 0;
        duration = 'Flat Rate';
      }
      return [item.name, duration, formatCurrency(total)];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['Equipment', 'Duration/Rate', 'Total']],
      body: equipBody,
      theme: 'grid',
      headStyles: { fillColor: colors.secondary, textColor: 255 },
      alternateRowStyles: { fillColor: colors.headerBg },
      margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Additional Costs
  if (additionalCosts && Object.values(additionalCosts).some(v => parseFloat(v) > 0)) {
    checkPageBreak(50);
    doc.setFontSize(12);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('ADDITIONAL COSTS', 14, yPos);
    yPos += 8;
    doc.setTextColor(...colors.text);

    const addBody = [];
    if (parseFloat(additionalCosts.permits) > 0) addBody.push(['Permits', formatCurrency(additionalCosts.permits)]);
    if (parseFloat(additionalCosts.insurance) > 0) addBody.push(['Insurance', formatCurrency(additionalCosts.insurance)]);
    if (parseFloat(additionalCosts.bonding) > 0) addBody.push(['Bonding', formatCurrency(additionalCosts.bonding)]);
    if (parseFloat(additionalCosts.overhead) > 0) addBody.push(['Overhead', formatCurrency(additionalCosts.overhead)]);
    if (parseFloat(additionalCosts.other) > 0) addBody.push(['Other', formatCurrency(additionalCosts.other)]);

    if (addBody.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Cost Type', 'Amount']],
        body: addBody,
        theme: 'grid',
        headStyles: { fillColor: colors.secondary, textColor: 255 },
        alternateRowStyles: { fillColor: colors.headerBg },
        margin: { left: 14, right: 14 },
        tableWidth: 100,
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }
  }

  // Line Item Markups
  if (lineItemMarkups && Object.values(lineItemMarkups).some(v => v > 0)) {
    checkPageBreak(50);
    doc.setFontSize(12);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('LINE ITEM MARKUPS', 14, yPos);
    yPos += 8;
    doc.setTextColor(...colors.text);

    const markupBody = [];
    Object.entries(lineItemMarkups).forEach(([key, value]) => {
      if (value > 0) {
        const markupAmount = estimateTotals.lineItemMarkupAmounts?.[key] || 0;
        markupBody.push([key.charAt(0).toUpperCase() + key.slice(1), `${value}%`, formatCurrency(markupAmount)]);
      }
    });

    if (markupBody.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Category', 'Markup %', 'Amount']],
        body: markupBody,
        theme: 'grid',
        headStyles: { fillColor: colors.secondary, textColor: 255 },
        alternateRowStyles: { fillColor: colors.headerBg },
        margin: { left: 14, right: 14 },
        tableWidth: 120,
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }
  }

  // Final Summary
  checkPageBreak(80);
  doc.addPage();
  yPos = 20;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.primary);
  doc.text('ESTIMATE SUMMARY', pageWidth / 2, yPos, { align: 'center' });
  yPos += 15;
  doc.setTextColor(...colors.text);

  const summaryData = [
    ['Calculator Data', formatCurrency(totals.calculatorSubtotal || totals.materialsSubtotal || 0)],
    ['Additional Materials (with waste)', formatCurrency(totals.materialsWithWaste || 0)],
    ['Labor', formatCurrency(totals.laborSubtotal || totals.laborTotal || 0)],
    ['Equipment', formatCurrency(totals.equipmentSubtotal || totals.equipmentTotal || 0)],
    ['Additional Costs', formatCurrency(totals.additionalSubtotal || totals.otherTotal || 0)],
    ['', ''],
    ['SUBTOTAL', formatCurrency(totals.subtotal || 0)],
    [`Contingency (${settings.contingency || 0}%)`, formatCurrency(totals.contingencyAmount || 0)],
  ];

  // Only show tax if not hidden
  if (!settings.hideTaxes && settings.taxRate > 0) {
    summaryData.push([`Tax (${settings.taxRate}%)`, formatCurrency(totals.taxAmount || 0)]);
  }

  if (totals.totalLineItemMarkup > 0) {
    summaryData.push(['Line Item Markups', formatCurrency(totals.totalLineItemMarkup)]);
  }

  summaryData.push([`Global Markup (${settings.markupPercent || settings.markup || 0}%)`, formatCurrency(totals.globalMarkupAmount || totals.markupAmount || 0)]);

  autoTable(doc, {
    startY: yPos,
    body: summaryData,
    theme: 'plain',
    styles: { fontSize: 11, textColor: colors.text },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { halign: 'right', cellWidth: 50 }
    },
    margin: { left: 40, right: 40 },
  });
  yPos = doc.lastAutoTable.finalY + 10;

  // Grand Total Box with template colors
  doc.setFillColor(...colors.primary);
  doc.roundedRect(40, yPos, pageWidth - 80, 30, 3, 3, 'F');
  doc.setTextColor(255);
  doc.setFontSize(14);
  doc.text('TOTAL ESTIMATE', pageWidth / 2 - 30, yPos + 12);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(totals.grandTotal || 0), pageWidth / 2 + 30, yPos + 22);

  yPos += 40;
  doc.setTextColor(...colors.muted);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const profitMargin = totals.profitMargin || 0;
  doc.text(`Profit Margin: ${profitMargin.toFixed(1)}%`, pageWidth / 2, yPos, { align: 'center' });

  // Footer with template color
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...colors.muted);
    doc.text(
      `Generated by Mass Master Pro - ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  return doc;
};

/**
 * Download PDF report with enhanced error handling
 */
export const downloadPDFReport = async (data) => {
  try {
    console.log('🔽 Generating PDF report...');
    const doc = generatePDFReport(data);
    const fileName = `${data.projectInfo.name || 'Project'}_Estimate_${new Date().toISOString().split('T')[0]}.pdf`;
    
    // Generate blob
    const pdfBlob = doc.output('blob');
    console.log(`📄 PDF blob created: ${pdfBlob.size} bytes`);
    
    // Use guaranteed download
    await guaranteedDownload(pdfBlob, fileName);
    
    console.log(`✅ PDF downloaded: ${fileName}`);
    return true;
  } catch (error) {
    console.error('❌ PDF generation error:', error);
    throw error;
  }
};

/**
 * Get PDF as base64 for email
 */
export const getPDFBase64 = (data, filename, template = 'professional') => {
  const doc = generatePDFReport({ ...data, template });
  return doc.output('datauristring').split(',')[1];
};

/**
 * Generate Word document
 */
export const generateWordDocument = async ({
  projectInfo = {},
  companyInfo = {},
  calculatorTotals = {},
  estimateTotals,
  grandTotals,
  materialItems,
  laborItems,
  equipmentItems,
  additionalCosts,
  otherCosts,
  settings = {},
  lineItemMarkups
}) => {
  // Use grandTotals if estimateTotals not provided (for compatibility)
  const totals = estimateTotals || grandTotals || {};
  
  const children = [];

  // Title
  children.push(
    new Paragraph({
      text: 'MASS MASTER PRO',
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      text: 'PROJECT ESTIMATE',
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({ text: '' }),
  );

  // Project Information
  children.push(
    new Paragraph({
      text: 'PROJECT INFORMATION',
      heading: HeadingLevel.HEADING_2,
    }),
  );

  const projectTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      createTableRow('Project Name:', projectInfo.name || 'N/A'),
      createTableRow('Project Number:', projectInfo.number || 'N/A'),
      createTableRow('Date:', projectInfo.date || new Date().toLocaleDateString()),
      createTableRow('Location:', projectInfo.location || 'N/A'),
      createTableRow('Client:', projectInfo.client || projectInfo.clientName || 'N/A'),
      createTableRow('Client Email:', projectInfo.clientEmail || 'N/A'),
    ],
  });
  children.push(projectTable, new Paragraph({ text: '' }));

  // Company Information
  children.push(
    new Paragraph({
      text: 'COMPANY INFORMATION',
      heading: HeadingLevel.HEADING_2,
    }),
  );

  const companyTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      createTableRow('Company:', (companyInfo && companyInfo.name) || 'N/A'),
      createTableRow('Phone:', (companyInfo && companyInfo.phone) || 'N/A'),
      createTableRow('Email:', (companyInfo && companyInfo.email) || 'N/A'),
      createTableRow('License:', (companyInfo && companyInfo.license) || 'N/A'),
    ],
  });
  children.push(companyTable, new Paragraph({ text: '' }));

  // Calculator Summary
  const calcItems = [];
  if (calculatorTotals.demo > 0) calcItems.push(['Demo Materials', formatCurrency(calculatorTotals.demo)]);
  if (calculatorTotals.piping > 0) calcItems.push(['Oil Field Piping', formatCurrency(calculatorTotals.piping)]);
  if (calculatorTotals.tanks > 0) calcItems.push(['Oil Field Tanks', formatCurrency(calculatorTotals.tanks)]);
  if (calculatorTotals.buildings > 0) calcItems.push(['Custom Buildings', formatCurrency(calculatorTotals.buildings)]);
  if (calculatorTotals.import > 0) calcItems.push(['Import Materials', formatCurrency(calculatorTotals.import)]);
  if (calculatorTotals.customImport > 0) calcItems.push(['Custom Import Volume', formatCurrency(calculatorTotals.customImport)]);
  if (calculatorTotals.excavation > 0) calcItems.push(['Excavation', formatCurrency(calculatorTotals.excavation)]);
  if (calculatorTotals.customExcavation > 0) calcItems.push(['Custom Excavation Volume', formatCurrency(calculatorTotals.customExcavation)]);
  if (calculatorTotals.trucking > 0) calcItems.push(['Trucking', formatCurrency(calculatorTotals.trucking)]);
  if (calculatorTotals.disposal > 0) calcItems.push(['Disposal', formatCurrency(calculatorTotals.disposal)]);

  if (calcItems.length > 0) {
    children.push(
      new Paragraph({
        text: 'CALCULATOR SUMMARY',
        heading: HeadingLevel.HEADING_2,
      }),
    );

    const calcTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            createHeaderCell('Category'),
            createHeaderCell('Amount'),
          ],
        }),
        ...calcItems.map(([cat, amt]) => createTableRow(cat, amt)),
      ],
    });
    children.push(calcTable, new Paragraph({ text: '' }));
  }

  // Additional Materials
  if (materialItems && materialItems.length > 0) {
    children.push(
      new Paragraph({
        text: 'ADDITIONAL MATERIALS',
        heading: HeadingLevel.HEADING_2,
      }),
    );

    const matTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            createHeaderCell('Material'),
            createHeaderCell('Qty'),
            createHeaderCell('Unit'),
            createHeaderCell('Unit Cost'),
            createHeaderCell('Total'),
          ],
        }),
        ...materialItems.map(item => new TableRow({
          children: [
            createCell(item.name),
            createCell(String(item.quantity)),
            createCell(item.unit),
            createCell(formatCurrency(item.unitCost)),
            createCell(formatCurrency(item.quantity * item.unitCost)),
          ],
        })),
      ],
    });
    children.push(matTable, new Paragraph({ text: '' }));
  }

  // Labor
  if (laborItems && laborItems.length > 0) {
    children.push(
      new Paragraph({
        text: 'LABOR',
        heading: HeadingLevel.HEADING_2,
      }),
    );

    const laborTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            createHeaderCell('Position'),
            createHeaderCell('Reg Hours'),
            createHeaderCell('Rate'),
            createHeaderCell('OT Hours'),
            createHeaderCell('Total'),
          ],
        }),
        ...laborItems.map(item => {
          const regularCost = item.regularHours * item.hourlyRate;
          const overtimeCost = (item.overtimeHours || 0) * (item.overtimeRate || item.hourlyRate * 1.5);
          return new TableRow({
            children: [
              createCell(item.name),
              createCell(String(item.regularHours)),
              createCell(formatCurrency(item.hourlyRate)),
              createCell(String(item.overtimeHours || 0)),
              createCell(formatCurrency(regularCost + overtimeCost)),
            ],
          });
        }),
      ],
    });
    children.push(laborTable, new Paragraph({ text: '' }));
  }

  // Equipment
  if (equipmentItems && equipmentItems.length > 0) {
    children.push(
      new Paragraph({
        text: 'EQUIPMENT',
        heading: HeadingLevel.HEADING_2,
      }),
    );

    const equipTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            createHeaderCell('Equipment'),
            createHeaderCell('Duration/Rate'),
            createHeaderCell('Total'),
          ],
        }),
        ...equipmentItems.map(item => {
          let total = 0;
          let duration = '';
          if (item.rentalType === 'daily') {
            total = item.days * item.dailyRate;
            duration = `${item.days} days @ ${formatCurrency(item.dailyRate)}/day`;
          } else if (item.rentalType === 'weekly') {
            total = item.weeks * item.weeklyRate;
            duration = `${item.weeks} weeks @ ${formatCurrency(item.weeklyRate)}/week`;
          } else {
            total = item.flatRate || 0;
            duration = 'Flat Rate';
          }
          return new TableRow({
            children: [
              createCell(item.name),
              createCell(duration),
              createCell(formatCurrency(total)),
            ],
          });
        }),
      ],
    });
    children.push(equipTable, new Paragraph({ text: '' }));
  }

  // Estimate Summary
  children.push(
    new Paragraph({
      text: 'ESTIMATE SUMMARY',
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({ text: '' }),
  );

  const summaryItems = [
    ['Calculator Data', formatCurrency(totals.calculatorSubtotal || totals.materialsSubtotal || 0)],
    ['Additional Materials (with waste)', formatCurrency(totals.materialsWithWaste || 0)],
    ['Labor', formatCurrency(totals.laborSubtotal || totals.laborTotal || 0)],
    ['Equipment', formatCurrency(totals.equipmentSubtotal || totals.equipmentTotal || 0)],
    ['Additional Costs', formatCurrency(totals.additionalSubtotal || totals.otherTotal || 0)],
    ['', ''],
    ['SUBTOTAL', formatCurrency(totals.subtotal || 0)],
    [`Contingency (${settings.contingency || 0}%)`, formatCurrency(totals.contingencyAmount || 0)],
  ];

  // Only show tax if not hidden
  if (!settings.hideTaxes && settings.taxRate > 0) {
    summaryItems.push([`Tax (${settings.taxRate}%)`, formatCurrency(totals.taxAmount || 0)]);
  }

  if (totals.totalLineItemMarkup > 0) {
    summaryItems.push(['Line Item Markups', formatCurrency(totals.totalLineItemMarkup)]);
  }

  summaryItems.push([`Global Markup (${settings.markupPercent || settings.markup || 0}%)`, formatCurrency(totals.globalMarkupAmount || totals.markupAmount || 0)]);
  summaryItems.push(['', '']);
  summaryItems.push(['TOTAL ESTIMATE', formatCurrency(totals.grandTotal || 0)]);
  const profitMargin = totals.profitMargin || 0;
  summaryItems.push(['Profit Margin', `${profitMargin.toFixed(1)}%`]);

  const summaryTable = new Table({
    width: { size: 80, type: WidthType.PERCENTAGE },
    rows: summaryItems.map(([label, value]) => createTableRow(label, value, label === 'TOTAL ESTIMATE')),
  });
  children.push(summaryTable, new Paragraph({ text: '' }));

  // Footer
  children.push(
    new Paragraph({
      text: `Generated by Mass Master Pro - ${new Date().toLocaleString()}`,
      alignment: AlignmentType.CENTER,
    }),
  );

  const doc = new Document({
    sections: [{
      children: children,
    }],
  });

  return doc;
};

// Helper functions for Word document
const createTableRow = (label, value, bold = false) => {
  return new TableRow({
    children: [
      new TableCell({
        children: [new Paragraph({
          children: [new TextRun({ text: label, bold: true })],
        })],
        width: { size: 50, type: WidthType.PERCENTAGE },
      }),
      new TableCell({
        children: [new Paragraph({
          children: [new TextRun({ text: String(value), bold: bold })],
          alignment: AlignmentType.RIGHT,
        })],
        width: { size: 50, type: WidthType.PERCENTAGE },
      }),
    ],
  });
};

const createHeaderCell = (text) => {
  return new TableCell({
    children: [new Paragraph({
      children: [new TextRun({ text, bold: true })],
    })],
    shading: { fill: '2563EB' },
  });
};

const createCell = (text) => {
  return new TableCell({
    children: [new Paragraph({ text: String(text) })],
  });
};

/**
 * Download Word document
 */
export const downloadWordDocument = async (data) => {
  try {
    console.log('🔽 Generating Word document...');
    const doc = await generateWordDocument(data);
    const blob = await Packer.toBlob(doc);
    const fileName = `${data.projectInfo.name || 'Project'}_Estimate_${new Date().toISOString().split('T')[0]}.docx`;
    
    console.log(`📄 Word blob created: ${blob.size} bytes`);
    
    // Use guaranteed download
    await guaranteedDownload(blob, fileName);
    
    console.log(`✅ Word document downloaded: ${fileName}`);
    return true;
  } catch (error) {
    console.error('❌ Word document generation error:', error);
    throw error;
  }
};

/**
 * Get Word document as base64 for email
 */
export const getWordBase64 = async (data) => {
  const doc = await generateWordDocument(data);
  const arrayBuffer = await Packer.toBuffer(doc);
  const uint8Array = new Uint8Array(arrayBuffer);
  let binary = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
};

/**
 * Generate comprehensive Excel report with all estimate data
 */
export const generateComprehensiveExcel = ({
  projectInfo = {},
  companyInfo = {},
  calculatorTotals = {},
  estimateTotals,
  grandTotals,
  materialItems,
  laborItems,
  equipmentItems,
  additionalCosts,
  otherCosts,
  settings = {},
  lineItemMarkups
}) => {
  // Use grandTotals if estimateTotals not provided (for compatibility)
  const totals = estimateTotals || grandTotals || {};
  
  const wb = XLSX.utils.book_new();

  // Summary Sheet
  const summaryData = [
    ['MASS MASTER PRO - PROJECT ESTIMATE'],
    ['Generated:', new Date().toLocaleString()],
    [],
    ['PROJECT INFORMATION'],
    ['Project Name:', projectInfo.name || ''],
    ['Project Number:', projectInfo.number || ''],
    ['Date:', projectInfo.date || ''],
    ['Location:', projectInfo.location || ''],
    ['Client:', projectInfo.client || projectInfo.clientName || ''],
    ['Client Email:', projectInfo.clientEmail || ''],
    [],
    ['COMPANY INFORMATION'],
    ['Company:', (companyInfo && companyInfo.name) || ''],
    ['Phone:', (companyInfo && companyInfo.phone) || ''],
    ['Email:', (companyInfo && companyInfo.email) || ''],
    ['License:', (companyInfo && companyInfo.license) || ''],
    [],
    ['CALCULATOR SUMMARY'],
  ];

  if (calculatorTotals.demo > 0) summaryData.push(['Demo Materials:', formatCurrency(calculatorTotals.demo)]);
  if (calculatorTotals.piping > 0) summaryData.push(['Oil Field Piping:', formatCurrency(calculatorTotals.piping)]);
  if (calculatorTotals.tanks > 0) summaryData.push(['Oil Field Tanks:', formatCurrency(calculatorTotals.tanks)]);
  if (calculatorTotals.buildings > 0) summaryData.push(['Custom Buildings:', formatCurrency(calculatorTotals.buildings)]);
  if (calculatorTotals.import > 0) summaryData.push(['Import Materials:', formatCurrency(calculatorTotals.import)]);
  if (calculatorTotals.customImport > 0) summaryData.push(['Custom Import Volume:', formatCurrency(calculatorTotals.customImport)]);
  if (calculatorTotals.excavation > 0) summaryData.push(['Excavation:', formatCurrency(calculatorTotals.excavation)]);
  if (calculatorTotals.customExcavation > 0) summaryData.push(['Custom Excavation Volume:', formatCurrency(calculatorTotals.customExcavation)]);
  if (calculatorTotals.trucking > 0) summaryData.push(['Trucking:', formatCurrency(calculatorTotals.trucking)]);
  if (calculatorTotals.disposal > 0) summaryData.push(['Disposal:', formatCurrency(calculatorTotals.disposal)]);
  if (calculatorTotals.civil > 0) summaryData.push(['Civil Construction:', formatCurrency(calculatorTotals.civil)]);
  if (calculatorTotals.environmental > 0) summaryData.push(['Environmental:', formatCurrency(calculatorTotals.environmental)]);

  summaryData.push(
    [],
    ['ESTIMATE SUMMARY'],
    ['Calculator Data:', formatCurrency(totals.calculatorSubtotal || totals.materialsSubtotal || 0)],
    ['Additional Materials (with waste):', formatCurrency(totals.materialsWithWaste || 0)],
    ['Labor:', formatCurrency(totals.laborSubtotal || totals.laborTotal || 0)],
    ['Equipment:', formatCurrency(totals.equipmentSubtotal || totals.equipmentTotal || 0)],
    ['Additional Costs:', formatCurrency(totals.additionalSubtotal || totals.otherTotal || 0)],
    ['SUBTOTAL:', formatCurrency(totals.subtotal || 0)],
    [`Contingency (${settings.contingency || 0}%):`, formatCurrency(totals.contingencyAmount || 0)],
  );

  // Only show tax if not hidden
  if (!settings.hideTaxes && settings.taxRate > 0) {
    summaryData.push([`Tax (${settings.taxRate}%):`, formatCurrency(totals.taxAmount || 0)]);
  }

  if (totals.totalLineItemMarkup > 0) {
    summaryData.push(['Line Item Markups:', formatCurrency(totals.totalLineItemMarkup)]);
  }

  const profitMargin = totals.profitMargin || 0;
  summaryData.push(
    [`Global Markup (${settings.markupPercent || settings.markup || 0}%):`, formatCurrency(totals.globalMarkupAmount || totals.markupAmount || 0)],
    [],
    ['TOTAL ESTIMATE:', formatCurrency(totals.grandTotal || 0)],
    ['Profit Margin:', `${profitMargin.toFixed(1)}%`],
  );

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [{ wch: 35 }, { wch: 25 }];
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  // Additional Materials Sheet
  if (materialItems && materialItems.length > 0) {
    const matData = [
      ['ADDITIONAL MATERIALS'],
      ['Material', 'Quantity', 'Unit', 'Unit Cost', 'Total Cost'],
      ...materialItems.map(item => [
        item.name,
        item.quantity,
        item.unit,
        item.unitCost,
        item.quantity * item.unitCost
      ]),
      [],
      ['TOTAL', '', '', '', estimateTotals.materialsSubtotal],
    ];
    const matSheet = XLSX.utils.aoa_to_sheet(matData);
    matSheet['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, matSheet, 'Materials');
  }

  // Labor Sheet
  if (laborItems && laborItems.length > 0) {
    const laborData = [
      ['LABOR'],
      ['Position', 'Regular Hours', 'Hourly Rate', 'OT Hours', 'OT Rate', 'Total Cost'],
      ...laborItems.map(item => {
        const regularCost = item.regularHours * item.hourlyRate;
        const overtimeCost = (item.overtimeHours || 0) * (item.overtimeRate || item.hourlyRate * 1.5);
        return [
          item.name,
          item.regularHours,
          item.hourlyRate,
          item.overtimeHours || 0,
          item.overtimeRate || item.hourlyRate * 1.5,
          regularCost + overtimeCost
        ];
      }),
      [],
      ['TOTAL', '', '', '', '', estimateTotals.laborSubtotal],
    ];
    const laborSheet = XLSX.utils.aoa_to_sheet(laborData);
    laborSheet['!cols'] = [{ wch: 25 }, { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, laborSheet, 'Labor');
  }

  // Equipment Sheet
  if (equipmentItems && equipmentItems.length > 0) {
    const equipData = [
      ['EQUIPMENT'],
      ['Equipment', 'Rental Type', 'Duration', 'Rate', 'Total Cost'],
      ...equipmentItems.map(item => {
        let total = 0;
        let duration = '';
        let rate = 0;
        if (item.rentalType === 'daily') {
          total = item.days * item.dailyRate;
          duration = `${item.days} days`;
          rate = item.dailyRate;
        } else if (item.rentalType === 'weekly') {
          total = item.weeks * item.weeklyRate;
          duration = `${item.weeks} weeks`;
          rate = item.weeklyRate;
        } else {
          total = item.flatRate || 0;
          duration = 'Flat';
          rate = item.flatRate || 0;
        }
        return [item.name, item.rentalType, duration, rate, total];
      }),
      [],
      ['TOTAL', '', '', '', estimateTotals.equipmentSubtotal],
    ];
    const equipSheet = XLSX.utils.aoa_to_sheet(equipData);
    equipSheet['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, equipSheet, 'Equipment');
  }

  // Line Item Markups Sheet
  if (lineItemMarkups && Object.values(lineItemMarkups).some(v => v > 0)) {
    const markupData = [
      ['LINE ITEM MARKUPS'],
      ['Category', 'Markup %', 'Base Amount', 'Markup Amount'],
    ];

    Object.entries(lineItemMarkups).forEach(([key, value]) => {
      if (value > 0) {
        const markupAmount = totals.lineItemMarkupAmounts?.[key] || 0;
        let baseAmount = 0;
        if (key === 'materials') baseAmount = totals.materialsWithWaste || 0;
        else if (key === 'labor') baseAmount = totals.laborSubtotal || totals.laborTotal || 0;
        else if (key === 'equipment') baseAmount = totals.equipmentSubtotal || totals.equipmentTotal || 0;
        else if (calculatorTotals[key]) baseAmount = calculatorTotals[key];
        
        markupData.push([
          key.charAt(0).toUpperCase() + key.slice(1),
          `${value}%`,
          baseAmount,
          markupAmount
        ]);
      }
    });

    markupData.push([], ['TOTAL LINE ITEM MARKUP', '', '', totals.totalLineItemMarkup || 0]);

    const markupSheet = XLSX.utils.aoa_to_sheet(markupData);
    markupSheet['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, markupSheet, 'Markups');
  }

  return wb;
};

/**
 * Download comprehensive Excel report
 */
export const downloadComprehensiveExcel = async (data) => {
  try {
    console.log('🔽 Generating Excel report...');
    const wb = generateComprehensiveExcel(data);
    const fileName = `${data.projectInfo.name || 'Project'}_Estimate_${new Date().toISOString().split('T')[0]}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    console.log(`📄 Excel blob created: ${blob.size} bytes`);
    
    // Use guaranteed download
    await guaranteedDownload(blob, fileName);
    
    console.log(`✅ Excel spreadsheet downloaded: ${fileName}`);
    return true;
  } catch (error) {
    console.error('❌ Excel generation error:', error);
    throw error;
  }
};

/**
 * Get Excel as base64 for email
 */
export const getExcelBase64 = (data) => {
  const wb = generateComprehensiveExcel(data);
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'base64' });
  return wbout;
};

/**
 * Copy report text to clipboard
 */
export const copyReportToClipboard = async (reportText) => {
  try {
    await navigator.clipboard.writeText(reportText);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};
